import { useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

/**
 * Apply `?tab=` from the URL on entry, and again on every NAVIGATION that carries it — an
 * in-page link that sets `?tab=` (an inspection's linked component → "?tab=components&component=…",
 * a component's linked inspection → "?tab=inspections", 2026-10-12) must switch tabs while the
 * record is open, the second time too (the tab clicked in between leaves the URL as it was, so
 * the value alone cannot tell a new link from an old one). Keyed on the navigation
 * (`location.key`): a re-render, or a tab the user clicks afterwards, never re-applies it.
 * - `?tab=files` / `?tab=documents` → `filesTabIndex` (may resolve after tabs build)
 * - `?tab=2` → numeric index
 * - `?tab=<label>` → the tab whose label matches, case-insensitive, when `tabLabels` is given
 *   (2026-10-07: the Components record's Close returns to an aircraft's "?tab=components")
 *
 * Used so list "Open Files Tab" can deep-link into the Files panel.
 */
export function useTabFromQuery(
  setTab: (index: number) => void,
  filesTabIndex: number | null | undefined,
  tabLabels?: ReadonlyArray<string>
): void {
  const [searchParams] = useSearchParams();
  const { key: navigation } = useLocation();
  // the navigation whose `?tab=` was last applied (null: none yet)
  const applied = useRef<string | null>(null);

  useEffect(() => {
    const raw = searchParams.get("tab");
    if (raw == null || raw === "" || navigation === applied.current) return;

    if (raw === "files" || raw === "documents") {
      if (filesTabIndex != null && filesTabIndex >= 0) {
        setTab(filesTabIndex);
        applied.current = navigation;
      }
      return;
    }

    const n = Number(raw);
    if (!Number.isNaN(n) && n >= 0) {
      setTab(n);
      applied.current = navigation;
      return;
    }

    const byLabel = (tabLabels ?? []).findIndex((l) => l.toLowerCase() === raw.toLowerCase());
    if (byLabel >= 0) {
      setTab(byLabel);
      applied.current = navigation;
    }
  }, [searchParams, navigation, setTab, filesTabIndex, tabLabels]);
}

function resolveFilesTabIndex(
  tabs: Array<{ key?: string; label?: string }>
): number | null {
  const idx = tabs.findIndex(
    (t) =>
      t.key === "files" ||
      t.key === "documents" ||
      t.label === "Files" ||
      t.label === "Documents"
  );
  return idx >= 0 ? idx : null;
}

/** Drop inside a Formik/render body once `tabs` is known. */
export function FilesTabQueryApplier({
  tabs,
  setTab,
}: {
  tabs: Array<{ key?: string; label?: string }>;
  setTab: (index: number) => void;
}) {
  useTabFromQuery(setTab, resolveFilesTabIndex(tabs));
  return null;
}
